import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { HttpService } from '../../../core/services/http.service';
import { MockBackend } from '../../../core/mocks/mock-backend';
import {
  clearStoredToken,
  getStoredToken,
  setStoredToken,
} from '../../../core/services/token.storage';
import type { LoginCredentials, LoginResponse, User } from '../models/auth.model';

/** Messages lisibles associés aux codes d'erreur renvoyés par le backend. */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'user.errorLogin': 'Login ou mot de passe incorrects.',
};

/** Traduit les codes d'erreur du backend en message compréhensible pour l'utilisateur. */
function toLoginErrorMessage(errors: string[]): string {
  for (const code of errors) {
    const message = AUTH_ERROR_MESSAGES[code];
    if (message) {
      return message;
    }
  }
  return 'Une erreur est survenue lors de la connexion.';
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(HttpService);
  private readonly mock = inject(MockBackend);

  private readonly user = signal<User | null>(null);
  private readonly token = signal<string | null>(getStoredToken());

  /** Utilisateur connecté (null si non authentifié). */
  readonly currentUser = this.user.asReadonly();

  /** JWT courant (null si non authentifié). */
  readonly currentToken = this.token.asReadonly();

  /** Authentifie l'utilisateur et stocke le JWT retourné par le backend. */
  login(credentials: LoginCredentials): Observable<LoginResponse> {
    // Mode démo : connexion locale sans backend (badge « Mode démo » affiché).
    const source = this.mock.isMock
      ? this.mock.login()
      : this.api.post<LoginResponse>('/user/login', {
          login: credentials.login,
          password: credentials.password,
        });

    return source.pipe(
      tap((response) => {
        if (response.errors?.length) {
          throw new Error(toLoginErrorMessage(response.errors));
        }
        const token = response.tokenResponse?.token;
        if (token) {
          setStoredToken(token);
          this.token.set(token);
        }
      }),
    );
  }

  logout(): void {
    clearStoredToken();
    this.token.set(null);
    this.user.set(null);
  }
}
